/**
 * The Promo-or-Cash rules (EN-11236, the control from EN-11211): one balance
 * pays the whole amount, never a split. Promo is pre-selected when it covers
 * the total, otherwise Cash; a balance that cannot cover it can't be picked.
 * When neither covers it, Buy is disabled and the shortfall is measured
 * against the larger balance.
 */
export type PaySource = 'promo' | 'cash';

/** $2 each, fixed in Phase 1 (10/27); matches shared/streaks/ledger.js on the server. */
export const FREEZE_PRICE = 2;
/** At most 3 BOUGHT freezes held at once; earned ones don't count (EN-11236). */
export const BOUGHT_CAP = 3;
/** The sheet's picks: 1, 2 or 3 at a time, no stepper (10/27). */
export const PICKS = [1, 2, 3];

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface PaymentState {
  total: number;
  promoCovers: boolean;
  cashCovers: boolean;
  /** The balance that pays, or null when neither covers the total. */
  source: PaySource | null;
  /** How much more the larger balance needs; 0 when one covers it. */
  shortfall: number;
}

export function paymentState(
  qty: number,
  promo: number,
  cash: number,
  picked: PaySource | null
): PaymentState {
  const total = round2(qty * FREEZE_PRICE);
  const promoCovers = promo + 1e-9 >= total;
  const cashCovers = cash + 1e-9 >= total;
  let source: PaySource | null = picked;
  if (source === 'promo' && !promoCovers) source = null;
  if (source === 'cash' && !cashCovers) source = null;
  if (!source) source = promoCovers ? 'promo' : cashCovers ? 'cash' : null;
  return {
    total,
    promoCovers,
    cashCovers,
    source,
    shortfall: source ? 0 : round2(total - Math.max(promo, cash))
  };
}

export const money = (n: number) => `$${n.toFixed(2)}`;
/** Whole dollars without cents ("$2", "$6"), as the sheet's picks and button read. */
export const price = (n: number) => (Number.isInteger(n) ? `$${n}` : money(n));
export const freezesWord = (n: number) => `${n} ${n === 1 ? 'freeze' : 'freezes'}`;
export const sourceName = (s: PaySource) => (s === 'cash' ? 'Cash balance' : 'Promo balance');
